import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usePartnerSettings } from '@/hooks/usePartnerSettings';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const DEFAULT_REMINDER_DAYS = [90, 60, 30, 14, 7];

export function PrequalificationSettings() {
  const { settings, isLoading, updateSettings } = usePartnerSettings();
  const [expiryDays, setExpiryDays] = useState(365);
  const [reminderDays, setReminderDays] = useState<number[]>(DEFAULT_REMINDER_DAYS);
  const [autoExpire, setAutoExpire] = useState(true);
  const [requireAnnualUpdate, setRequireAnnualUpdate] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings?.prequalification_settings) {
      const ps = settings.prequalification_settings;
      setExpiryDays(ps.expiry_days || 365);
      setReminderDays(ps.reminder_days_before_expiry || DEFAULT_REMINDER_DAYS);
      setAutoExpire(ps.auto_expire_on_document_expiry ?? true);
      setRequireAnnualUpdate(ps.require_annual_update ?? true);
    }
  }, [settings]);

  const handleSave = async () => {
    await updateSettings({
      prequalification_settings: {
        expiry_days: expiryDays,
        reminder_days_before_expiry: reminderDays,
        auto_expire_on_document_expiry: autoExpire,
        require_annual_update: requireAnnualUpdate,
      },
    });
    setHasChanges(false);
  };

  const toggleReminderDay = (day: number) => {
    const updated = reminderDays.includes(day)
      ? reminderDays.filter(d => d !== day)
      : [...reminderDays, day].sort((a, b) => b - a);
    setReminderDays(updated);
    setHasChanges(true);
  };

  if (isLoading) {
    return <Card><CardContent className="py-6">Loading...</CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Prequalification Settings</CardTitle>
        <CardDescription>
          Configure expiry periods and automatic notification settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="expiryDays">Prequalification Expiry Period (days)</Label>
          <Input
            id="expiryDays"
            type="number"
            value={expiryDays}
            onChange={(e) => {
              setExpiryDays(parseInt(e.target.value) || 365);
              setHasChanges(true);
            }}
            min="1"
          />
          <p className="text-xs text-muted-foreground">
            How long prequalification approval remains valid (default: 365 days)
          </p>
        </div>

        <div className="space-y-3">
          <Label>Reminder Schedule (days before expiry)</Label>
          <div className="flex flex-wrap gap-4">
            {[90, 60, 30, 14, 7, 3, 1].map((day) => (
              <div key={day} className="flex items-center space-x-2">
                <Checkbox
                  id={`reminder-${day}`}
                  checked={reminderDays.includes(day)}
                  onCheckedChange={() => toggleReminderDay(day)}
                />
                <Label htmlFor={`reminder-${day}`} className="font-normal cursor-pointer">
                  {day} days
                </Label>
              </div>
            ))}
          </div>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Automated notifications will be sent at the selected intervals before prequalification expires
            </AlertDescription>
          </Alert>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Auto-Expire on Document Expiry</Label>
              <p className="text-sm text-muted-foreground">
                Automatically expire prequalification if any required document expires
              </p>
            </div>
            <Switch
              checked={autoExpire}
              onCheckedChange={(checked) => {
                setAutoExpire(checked);
                setHasChanges(true);
              }}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Require Annual Update</Label>
              <p className="text-sm text-muted-foreground">
                Require contractors to resubmit documents annually even if not expired
              </p>
            </div>
            <Switch
              checked={requireAnnualUpdate}
              onCheckedChange={(checked) => {
                setRequireAnnualUpdate(checked);
                setHasChanges(true);
              }}
            />
          </div>
        </div>

        <Button onClick={handleSave} disabled={!hasChanges}>
          Save Settings
        </Button>
      </CardContent>
    </Card>
  );
}
